import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  Title,
  useList,
  useNotify,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';

import { useTitle } from '@src/hooks/useTitle';
import { DashboardResources } from '@src/types/constants';
import { pricingRulesDataProvider } from './dataProvider';
import WithPricingGuard from './WithPricingGuard';
import type { PricingHistoryEntry } from './types';

const RESOURCE = DashboardResources.PricingRules;
const LIST_PATH = `/${DashboardResources.PricingRules}`;

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Pricing-history view for Pricing Rules.
 *
 * Mounted as a sub-route of the resource at /resalePricing/pricingRules/history.
 * Receives no props — uses useNavigate for the "Return to Rules" action.
 *
 * For each history entry, "TSV" requests a presigned URL via the data provider's
 * generateExport method and triggers a direct browser download. We do NOT build
 * a Blob or call `URL.createObjectURL` — the TSV bytes never enter the JS heap
 * or React state. (How the browser itself buffers the download is out of scope.)
 *
 * Download safety guards (defense in depth — happy path is first-party):
 *  - Check `expiresAt` client-side and short-circuit with a notify before navigating.
 *    The presigned URL's TTL is short; a user can plausibly let the row sit longer
 *    than that between fetch and click.
 *  - Validate the URL scheme + host before navigating. `https:` is always
 *    accepted; `http:` is accepted only when the host is `localhost` or under
 *    `.localstack.cloud` (so local dev against LocalStack works). Anything
 *    else (`javascript:`, `data:`, `http://attacker.com/…`, etc.) is refused.
 *    The codebase has no other precedent for navigating to a server-supplied
 *    URL; this fails safe if a backend regression ever returned one.
 *  - Use `window.open(..., '_blank')` instead of mutating
 *    `window.location.href`. If S3 returns a 4xx, the error page lands in a new
 *    tab instead of replacing the dashboard. Notify on pop-up-blocker failures.
 */
const PricingRulesHistory = () => {
  const { title } = useTitle('Pricing Rules History');
  const navigate = useNavigate();
  const notify = useNotify();
  const [entries, setEntries] = useState<PricingHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    pricingRulesDataProvider
      .getHistory(RESOURCE, {} as Record<string, never>)
      .then(({ data }) => {
        if (cancelled) return;
        setEntries(data);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('PricingRulesHistory: fetch failed', {
          error: error instanceof Error ? error.message : error,
        });
        notify(
          error instanceof Error ? error.message : 'Failed to load history',
          { type: 'error' },
        );
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [notify]);

  const handleDownload = useCallback(
    async (entry: PricingHistoryEntry) => {
      setDownloadingId(entry.id);
      try {
        const { data } = await pricingRulesDataProvider.generateExport(
          RESOURCE,
          { id: entry.id },
        );

        // Expiry guard — the URL has a short TTL. If it's already past expiry
        // before we even try to navigate, fail loud rather than letting the user
        // hit an opaque S3 4xx.
        if (new Date(data.expiresAt).getTime() <= Date.now()) {
          notify('This download link has expired. Please try again.', {
            type: 'warning',
          });
          return;
        }

        // Scheme + host guard — there is no precedent in this codebase for
        // navigating to a server-supplied URL. Accept `https:` unconditionally;
        // accept `http:` only when the host is `localhost` or under
        // `.localstack.cloud` so local dev works against the LocalStack S3
        // emulator. Everything else (`javascript:`, `data:`,
        // `http://attacker.com/…`, etc.) is refused.
        let parsed: URL;
        try {
          parsed = new URL(data.url);
        } catch {
          notify('Received an invalid download URL.', { type: 'error' });
          return;
        }
        const isLocalDevTarget = parsed.protocol === 'http:' &&
          (parsed.hostname === 'localhost' ||
            parsed.hostname.endsWith('.localstack.cloud'));
        if (parsed.protocol !== 'https:' && !isLocalDevTarget) {
          notify('Received a non-HTTPS download URL.', { type: 'error' });
          return;
        }

        // Open the presigned URL in a new tab so an S3 error page can't replace
        // the dashboard. The S3 object's Content-Disposition: attachment header
        // drives the filename and tells the browser to download rather than
        // render — the new tab stays blank, so there's no rendered page with
        // running JS to exploit `window.opener`. That's why we deliberately do
        // NOT pass 'noopener' here: window.open returns null whenever that
        // feature is set, which makes popup-blocker detection impossible.
        // Trading the theoretical reverse-tabnabbing protection (no surface for
        // a binary download) for reliable popup-blocker UX is the right call.
        const opened = window.open(data.url, '_blank');
        if (!opened) {
          notify(
            'Could not start the download — please allow pop-ups for this site and try again.',
            { type: 'warning' },
          );
        }
      } catch (error) {
        console.error('PricingRulesHistory: export failed', {
          entryId: entry.id,
          error: error instanceof Error ? error.message : error,
        });
        notify(
          error instanceof Error ? error.message : 'Export failed',
          { type: 'error' },
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [notify],
  );

  const listContext = useList({ data: entries, isLoading });

  return (
    <WithPricingGuard>
      {/* react-admin's <Title> uses a React Portal (createPortal into
          `#react-admin-title`) to render into the AppBar (blue band) — same slot
          List pages use via their `title` prop. Without this, the string returned
          by the dashboard's useTitle hook would render inline in the body. */}
      <Title title={title} />
      <Box sx={{ p: 2 }}>
        <Button
          label="Return to Rules"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(LIST_PATH)}
          sx={{ mb: 2 }}
        />
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No pricing history found.
          </Typography>
        ) : (
          <ListContextProvider value={listContext}>
            <Datagrid bulkActionButtons={false} rowClick={false}>
              <FunctionField
                label="User"
                render={(r: PricingHistoryEntry) => r.user?.name ?? '-'}
              />
              <FunctionField
                label="Saved At"
                render={(r: PricingHistoryEntry) => formatDate(r.createdAt)}
              />
              <FunctionField
                label="Initiated"
                render={(r: PricingHistoryEntry) => formatDate(r.initiatedAt)}
              />
              <FunctionField
                label="Processed"
                render={(r: PricingHistoryEntry) => formatDate(r.processedAt)}
              />
              <FunctionField
                label="Download"
                render={(r: PricingHistoryEntry) => (
                  <Button
                    size="small"
                    label="TSV"
                    startIcon={
                      downloadingId === r.id ?
                        <CircularProgress size={14} /> :
                        <DownloadIcon />
                    }
                    disabled={downloadingId === r.id}
                    onClick={() => handleDownload(r)}
                  />
                )}
              />
            </Datagrid>
          </ListContextProvider>
        )}
      </Box>
    </WithPricingGuard>
  );
};

export default PricingRulesHistory;
